// AGC: Notifications API Service
// AGC: Handles all API calls related to notifications
// AGC: All notifications are filtered by user account - each user only sees their own notifications

import { getBackendUrl } from '../utils/backendUrl'
import type { Notification } from '../types'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

/**
 * AGC: Get all notifications from backend for the authenticated user
 * AGC: Requires authentication token - notifications are filtered by userId
 */
export const getNotifications = async (authToken?: string | null): Promise<Notification[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/notifications`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to fetch notifications: ${response.statusText}`)
    }

    const data = await response.json()
    return data.notifications || []
  } catch (error) {
    console.error('AGC: Error fetching notifications:', error)
    // AGC: Return empty array on error (graceful degradation) - new accounts start empty
    return []
  }
}

/**
 * AGC: Create a new notification for the authenticated user
 * AGC: Requires authentication token - notification is associated with userId
 */
export const createNotification = async (
  notificationData: Partial<Notification>,
  authToken?: string | null
): Promise<Notification> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/notifications`, {
      method: 'POST',
      headers,
      body: JSON.stringify(notificationData),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create notification: ${response.statusText}`)
    }

    const data = await response.json()
    return data.notification
  } catch (error) {
    console.error('AGC: Error creating notification:', error)
    throw error
  }
}

/**
 * AGC: Update a notification for the authenticated user (e.g., mark as read)
 */
export const updateNotification = async (
  notificationId: string,
  updateData: Partial<Notification>,
  authToken?: string | null
): Promise<Notification> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/notifications/${notificationId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update notification: ${response.statusText}`)
    }

    const data = await response.json()
    return data.notification
  } catch (error) {
    console.error('AGC: Error updating notification:', error)
    throw error
  }
}

/**
 * AGC: Mark all notifications as read for the authenticated user
 */
export const markAllNotificationsAsRead = async (authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/notifications/mark-all-read`, {
      method: 'PUT',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to mark all notifications as read: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error marking all notifications as read:', error)
    throw error
  }
}

/**
 * AGC: Delete a notification for the authenticated user
 */
export const deleteNotification = async (notificationId: string, authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/notifications/${notificationId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to delete notification: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting notification:', error)
    throw error
  }
}

/**
 * AGC: Delete all notifications for the authenticated user
 */
export const deleteAllNotifications = async (authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/notifications`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to delete all notifications: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting all notifications:', error)
    throw error
  }
}





